import ChevronDownIcon from '@shieldpay/bumblebee/icons/ChevronDownIcon';
import ChevronRightIcon from '@shieldpay/bumblebee/icons/ChevronRightIcon';
import CloseIcon from '@shieldpay/bumblebee/icons/CloseIcon';
import InfoIcon from '@shieldpay/bumblebee/icons/InfoIcon';
import ShowIcon from '@shieldpay/bumblebee/icons/ShowIcon';

import type { Button } from '@shieldpay/bumblebee/button';
import { Heading } from '@shieldpay/bumblebee/heading';
import { Column } from '@shieldpay/bumblebee/layout';
import type { Link } from '@shieldpay/bumblebee/link';
import { Table } from '@shieldpay/bumblebee/table';

export const ButtonAppearance = ({
  Component,
}: {
  Component: typeof Button | typeof Link;
}) => (
  <Column spacing="base">
    <Heading level="3" variant="title">
      Button styled links
    </Heading>

    <Table caption="button variants" style={{ tableLayout: 'fixed' }}>
      <Table.Head>
        <Table.Row>
          <Table.HeaderCell>Small button</Table.HeaderCell>
          <Table.HeaderCell>Medium button</Table.HeaderCell>
          <Table.HeaderCell>Large button</Table.HeaderCell>
        </Table.Row>
      </Table.Head>

      <Table.Body>
        <Table.Row>
          <Table.DataCell>
            <Component href="#" variant="solid" size="small">
              Solid button
            </Component>
          </Table.DataCell>

          <Table.DataCell>
            <Component href="#" variant="solid" size="medium">
              Solid button
            </Component>
          </Table.DataCell>
          <Table.DataCell>
            <Component href="#" variant="solid" size="large">
              Solid button
            </Component>
          </Table.DataCell>
        </Table.Row>
        <Table.Row>
          <Table.DataCell>
            <Component
              href="#"
              variant="solid"
              size="small"
              LeftIcon={InfoIcon}
              RightIcon={ChevronRightIcon}
            >
              Solid button
            </Component>
          </Table.DataCell>
          <Table.DataCell>
            <Component
              href="#"
              variant="solid"
              size="medium"
              LeftIcon={InfoIcon}
              RightIcon={ChevronRightIcon}
            >
              Solid button
            </Component>
          </Table.DataCell>
          <Table.DataCell>
            <Component
              href="#"
              variant="solid"
              size="large"
              LeftIcon={InfoIcon}
              RightIcon={ChevronRightIcon}
            >
              Solid button
            </Component>
          </Table.DataCell>
        </Table.Row>
        <Table.Row>
          <Table.DataCell>
            <Component
              href="#"
              variant="solid"
              size="small"
              RightIcon={InfoIcon}
            >
              Solid button
            </Component>
          </Table.DataCell>
          <Table.DataCell>
            <Component
              href="#"
              variant="solid"
              size="medium"
              RightIcon={InfoIcon}
            >
              Solid button
            </Component>
          </Table.DataCell>
          <Table.DataCell>
            <Component
              href="#"
              variant="solid"
              size="large"
              RightIcon={InfoIcon}
            >
              Solid button
            </Component>
          </Table.DataCell>
        </Table.Row>
        <Table.Row>
          <Table.DataCell>
            <Component
              disabled
              href="#"
              variant="solid"
              size="small"
              LeftIcon={InfoIcon}
            >
              Solid disabled
            </Component>
          </Table.DataCell>

          <Table.DataCell>
            <Component
              disabled
              href="#"
              variant="solid"
              size="medium"
              LeftIcon={InfoIcon}
            >
              Solid disabled
            </Component>
          </Table.DataCell>
          <Table.DataCell>
            <Component
              disabled
              href="#"
              variant="solid"
              size="large"
              LeftIcon={InfoIcon}
            >
              Solid disabled
            </Component>
          </Table.DataCell>
        </Table.Row>
        <Table.Row>
          <Table.DataCell align="left">
            <Component href="#" variant="outline" size="small">
              Outline button
            </Component>
          </Table.DataCell>
          <Table.DataCell>
            <Component href="#" variant="outline" size="medium">
              Outline button
            </Component>
          </Table.DataCell>
          <Table.DataCell>
            <Component href="#" variant="outline" size="large">
              Outline button
            </Component>
          </Table.DataCell>
        </Table.Row>
        <Table.Row>
          <Table.DataCell>
            <Component
              href="#"
              variant="outline"
              size="small"
              LeftIcon={InfoIcon}
              RightIcon={ChevronRightIcon}
            >
              Outline button
            </Component>
          </Table.DataCell>
          <Table.DataCell>
            <Component
              href="#"
              variant="outline"
              size="medium"
              LeftIcon={InfoIcon}
              RightIcon={ChevronRightIcon}
            >
              Outline button
            </Component>
          </Table.DataCell>
          <Table.DataCell>
            <Component
              href="#"
              variant="outline"
              size="large"
              LeftIcon={InfoIcon}
              RightIcon={ChevronRightIcon}
            >
              Outline button
            </Component>
          </Table.DataCell>
        </Table.Row>
        <Table.Row>
          <Table.DataCell>
            <Component
              href="#"
              variant="outline"
              size="small"
              RightIcon={InfoIcon}
            >
              Outline button
            </Component>
          </Table.DataCell>

          <Table.DataCell>
            <Component
              href="#"
              variant="outline"
              size="medium"
              RightIcon={InfoIcon}
            >
              Outline button
            </Component>
          </Table.DataCell>
          <Table.DataCell>
            <Component
              href="#"
              variant="outline"
              size="large"
              RightIcon={InfoIcon}
            >
              Outline button
            </Component>
          </Table.DataCell>
        </Table.Row>
        <Table.Row>
          <Table.DataCell>
            <Component
              disabled
              href="#"
              variant="outline"
              size="small"
              LeftIcon={InfoIcon}
            >
              Outline disabled
            </Component>
          </Table.DataCell>

          <Table.DataCell>
            <Component
              disabled
              href="#"
              variant="outline"
              size="medium"
              LeftIcon={InfoIcon}
            >
              Outline disabled
            </Component>
          </Table.DataCell>
          <Table.DataCell>
            <Component
              disabled
              href="#"
              variant="outline"
              size="large"
              LeftIcon={InfoIcon}
            >
              Outline disabled
            </Component>
          </Table.DataCell>
        </Table.Row>
      </Table.Body>
    </Table>

    <Heading level="3" variant="title">
      Icon buttons
    </Heading>

    <Table caption="icon buttons">
      <Table.Head>
        <Table.Row>
          <Table.HeaderCell>Small button</Table.HeaderCell>
          <Table.HeaderCell>Medium button</Table.HeaderCell>
          <Table.HeaderCell>Large button</Table.HeaderCell>
        </Table.Row>

        <Table.Row>
          <Table.DataCell>
            <Component
              href="#"
              size="small"
              variant="icon"
              Icon={ChevronDownIcon}
            >
              Show password
            </Component>
          </Table.DataCell>
          <Table.DataCell>
            <Component href="#" size="medium" variant="icon" Icon={ShowIcon}>
              Show password
            </Component>
          </Table.DataCell>
          <Table.DataCell>
            <Component href="#" size="large" variant="icon" Icon={CloseIcon}>
              Close
            </Component>
          </Table.DataCell>
        </Table.Row>
      </Table.Head>
    </Table>
  </Column>
);
